import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAdministrators, deleteAdministrator } from '@/api/administrators';
import type { Administrator } from '@/types/administrator';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, ShieldCheck, Check, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function AdministratorsPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [administrators, setAdministrators] = useState<Administrator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<string>('lastname');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [deleteAdmin, setDeleteAdmin] = useState<Administrator | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    fetchAdministrators();
  }, []);

  const fetchAdministrators = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdministrators();
      setAdministrators(data);
    } catch (err) {
      setError('Erreur lors du chargement des administrateurs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async () => {
    if (!deleteAdmin) return;

    try {
      setDeleting(true);
      await deleteAdministrator(deleteAdmin.id);
      setAdministrators(administrators.filter((a) => a.id !== deleteAdmin.id));
      setDeleteAdmin(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de l\'administrateur');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAndSortedAdministrators = useMemo(() => {
    let result = [...administrators];

    // Filter
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter(
        (a) =>
          a.fullname.toLowerCase().includes(search) ||
          a.email.toLowerCase().includes(search)
      );
    }

    // Sort
    result.sort((a, b) => {
      const aValue = a[sortBy as keyof Administrator];
      const bValue = b[sortBy as keyof Administrator];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortOrder === 'ASC'
          ? (aValue === bValue ? 0 : aValue ? -1 : 1)
          : (aValue === bValue ? 0 : aValue ? 1 : -1);
      }

      return 0;
    });

    return result;
  }, [administrators, searchValue, sortBy, sortOrder]);

  const columns: Column<Administrator>[] = [
    {
      key: 'fullname',
      header: 'Nom',
      sortable: true,
      render: (admin) => (
        <span className="font-medium text-gray-900">{admin.fullname}</span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (admin) => (
        <span className="text-gray-600">{admin.email}</span>
      ),
    },
    {
      key: 'enabled',
      header: 'Statut',
      sortable: true,
      render: (admin) => {
        if (!admin.enabled) {
          return <Badge variant="secondary">Desactive</Badge>;
        }
        if (admin.isExpired) {
          return <Badge variant="destructive">Expire</Badge>;
        }
        return <Badge variant="success">Actif</Badge>;
      },
    },
    {
      key: 'expiresAt',
      header: 'Expiration',
      render: (admin) => (
        <span className="text-gray-600 text-sm">
          {admin.expiresAt
            ? new Date(admin.expiresAt).toLocaleDateString('fr-FR')
            : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (admin) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(`/administrators/${admin.id}/edit`)}
            title="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteAdmin(admin)}
              title="Supprimer"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Administrateurs V2
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gérez les administrateurs ayant accès à toutes les écoles
          </p>
        </div>

        <Button asChild>
          <Link to="/administrators/new">
            <Plus className="h-4 w-4" />
            Nouvel administrateur
          </Link>
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredAndSortedAdministrators}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Rechercher par nom ou email..."
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        totalItems={filteredAndSortedAdministrators.length}
        emptyMessage="Aucun administrateur trouve"
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteAdmin} onOpenChange={() => setDeleteAdmin(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'administrateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer l'administrateur{' '}
              <strong>{deleteAdmin?.fullname}</strong> ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
